/**
 * buildBatchProductLabelEscPos.ts — Lote de etiquetas en un solo buffer ESC/POS.
 *
 * QUÉ HACE:
 *  - Construye los bytes de N productos × copias con corte automático entre
 *    etiquetas (GS V A). La ESTRUCTURA cambia según `design.layout`: centrado,
 *    banda, bloque dividido, precio dominante, ficha o minimal.
 *  - La térmica es monocroma: los colores del diseño no aplican aquí.
 *
 * QUÉ EXPORTA: BatchLabelEntry, buildBatchProductLabelEscPos
 * QUÉ NO HACE: no envía a la impresora (eso vive en posHardware).
 */

import {
  getTicketMaxWidth,
  loadBusinessForReceipt,
  sanitizeForThermal,
} from "@/features/caja/services/ticketRender.service";
import { formatCurrency } from "@/lib/utils/formatters";
import {
  resolveLabelPrice,
  type LabelDesign,
  type LabelProduct,
} from "./labelDesign";

const ESC = 0x1b;
const GS = 0x1d;

export interface BatchLabelEntry {
  product: LabelProduct;
  copies: number;
}

function clampCopies(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(999, Math.max(1, Math.round(value)));
}

/** Parte un texto en líneas de ancho `width` respetando palabras. */
function wrapWords(text: string, width: number): string[] {
  const clean = sanitizeForThermal(text.toUpperCase().trim());
  if (clean.length <= width) return [clean];
  const words = clean.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (!current) {
      current = word;
    } else if ((current + " " + word).length <= width) {
      current += " " + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/**
 * Buffer ESC/POS completo del lote. Cada etiqueta termina con avance y corte
 * parcial para que la cuchilla separe una a una.
 */
export function buildBatchProductLabelEscPos(
  entries: BatchLabelEntry[],
  design: LabelDesign,
  paperWidth: 58 | 80 = 58,
): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const maxWidth = getTicketMaxWidth(paperWidth);
  const nameWidth = Math.max(6, Math.floor(maxWidth / 2));
  const businessName = (loadBusinessForReceipt().nombre || "").trim();
  const layout = design.layout;
  const leftAligned = layout === "ficha" || layout === "minimal";
  const compact = design.textSize === "compacto";
  const large = design.textSize === "grande";
  const nameMode = compact ? 0x08 : 0x38;
  const nameWidthForLayout = leftAligned
    ? compact
      ? maxWidth
      : nameWidth
    : nameWidth;

  const add = (text: string) => {
    chunks.push(encoder.encode(sanitizeForThermal(text) + "\n"));
  };
  const addRaw = (bytes: number[]) => {
    chunks.push(new Uint8Array(bytes));
  };
  const align = (mode: "left" | "center") => {
    addRaw([ESC, 0x61, mode === "center" ? 0x01 : 0x00]);
  };
  const centerLine = (text: string) => {
    const clean = sanitizeForThermal(text);
    const value = clean.length >= maxWidth ? clean.slice(0, maxWidth) : clean;
    const pad = Math.floor((maxWidth - value.length) / 2);
    add(" ".repeat(pad) + value);
  };
  const leftLine = (text: string) => add(sanitizeForThermal(text));
  const rightLine = (text: string) => {
    const clean = sanitizeForThermal(text).slice(0, maxWidth);
    add(" ".repeat(Math.max(0, maxWidth - clean.length)) + clean);
  };
  const separator = (char: string) => add(char.repeat(maxWidth));

  addRaw([ESC, 0x40]); // Inicializar impresora

  for (const entry of entries) {
    const copies = clampCopies(entry.copies);
    const price = resolveLabelPrice(entry.product);

    for (let copy = 0; copy < copies; copy++) {
      align(leftAligned ? "left" : "center");

      if (design.showBusiness && businessName) {
        addRaw([ESC, 0x21, 0x01]); // Fuente B (reducida)
        if (leftAligned) leftLine(businessName);
        else centerLine(businessName);
        addRaw([ESC, 0x21, 0x00]);
      }

      if (design.showName) {
        addRaw([ESC, 0x21, nameMode]);
        const pushName = leftAligned ? leftLine : centerLine;
        wrapWords(entry.product.nombre, nameWidthForLayout)
          .slice(0, 3)
          .forEach(pushName);
        addRaw([ESC, 0x21, 0x00]);
      }

      if (layout === "banda") separator("=");
      if (layout === "centrado" && design.showDivider) separator("_");
      if (layout === "split" && design.showDivider) separator("-");

      if (design.showOldPrice && price.hasDiscount && price.previous !== null) {
        addRaw([ESC, 0x21, 0x01]);
        const oldText = `ANTES ${formatCurrency(price.previous)}`;
        if (leftAligned) leftLine(oldText);
        else centerLine(oldText);
        addRaw([ESC, 0x21, 0x00]);
      }

      if (large) addRaw([0x0a]); // Aire extra cuando el tamaño es grande
      addRaw([ESC, 0x21, 0x38]); // Precio destacado
      if (layout === "ficha") rightLine(formatCurrency(price.final));
      else if (leftAligned) leftLine(formatCurrency(price.final));
      else centerLine(formatCurrency(price.final));
      addRaw([ESC, 0x21, 0x00]);
      if (large) addRaw([0x0a]);

      if (layout === "banda") separator("=");

      if (design.showOffer && design.offerText.trim()) {
        addRaw([ESC, 0x21, 0x08]); // Negrita
        if (leftAligned) leftLine(design.offerText.trim());
        else centerLine(design.offerText.trim());
        addRaw([ESC, 0x21, 0x00]);
      }

      addRaw([0x0a, 0x0a, 0x0a]); // Avance antes del corte
      addRaw([GS, 0x56, 65, 0x10]); // Corte parcial con feed
    }
  }

  const totalLength = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}
